import type {
  AuthenticatedUser,
  FixedAssetsResult,
  FixedAssetsSnapshot,
  IntercompanyResult,
  IntercompanySnapshot,
  InventoryResult,
  InventorySnapshot,
  LeaseRouResult,
  LeaseRouSnapshot,
  ParsedUpload,
  PoAccrualResult,
  PoAccrualSnapshot,
  ReconSummary,
  SlotId,
  SlotState,
  VacationAccrualResult,
  VacationAccrualSnapshot,
  WorkpaperStatus,
} from "@ranfac/shared";

export interface WorkpaperState {
  id: string;
  reconId: string;
  periodEnd: string;
  status: WorkpaperStatus;
  preparerEmail: string | null;
  reviewerEmail: string | null;
  approverEmail: string | null;
  createdAt: string;
  updatedAt: string;
  lockedAt: string | null;
}

// Same-origin in dev (Vite proxies /api → :8787). In prod the Pages site and
// the Worker are on different origins, so set VITE_API_BASE at build time.
const API_BASE = import.meta.env.VITE_API_BASE ?? "";

function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

async function getJson<T>(path: string): Promise<T> {
  const r = await fetch(apiUrl(path), { credentials: "include" });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText} — ${path}`);
  return r.json() as Promise<T>;
}

export function fetchMe(): Promise<AuthenticatedUser> {
  return getJson("/api/me");
}

export function fetchRecons(): Promise<{ recons: ReconSummary[] }> {
  return getJson("/api/recons");
}

export function fetchPoAccrual(
  periodEnd: string,
): Promise<{
  snapshot: PoAccrualSnapshot;
  result: PoAccrualResult;
  source: "upload" | "fixture";
}> {
  return getJson(`/api/recons/po-accrual?periodEnd=${periodEnd}`);
}

export function fetchUploads(
  period: string,
): Promise<{ period: string; periodEnd: string; slots: SlotState[] }> {
  return getJson(`/api/close/${period}/uploads`);
}

export async function uploadSlot(
  period: string,
  slotId: SlotId,
  parsed: ParsedUpload,
): Promise<{
  ok: boolean;
  status: string;
  rowCount: number;
  errors: string[];
}> {
  const r = await fetch(apiUrl(`/api/close/${period}/uploads/${slotId}`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(parsed),
    credentials: "include",
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json();
}

export async function deleteUpload(
  period: string,
  slotId: SlotId,
): Promise<{ ok: boolean; removed: number }> {
  const r = await fetch(apiUrl(`/api/close/${period}/uploads/${slotId}`), {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json();
}

export function fetchVacationAccrual(
  periodEnd: string,
): Promise<{
  snapshot: VacationAccrualSnapshot;
  result: VacationAccrualResult;
  source: "upload" | "fixture";
}> {
  return getJson(`/api/recons/accrued-vacation?periodEnd=${periodEnd}`);
}

export async function downloadVacationJe(
  periodEnd: string,
): Promise<{ filename: string; csv: string }> {
  const r = await fetch(apiUrl("/api/recons/accrued-vacation/je.csv"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ periodEnd, postingDate: periodEnd }),
    credentials: "include",
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  const disposition = r.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? `je-vac-${periodEnd}.csv`;
  return { filename, csv: await r.text() };
}

export function fetchInventory(
  periodEnd: string,
): Promise<{
  snapshot: InventorySnapshot;
  result: InventoryResult;
  source: "upload" | "fixture";
}> {
  return getJson(`/api/recons/inventory?periodEnd=${periodEnd}`);
}

export async function downloadInventoryJe(
  periodEnd: string,
): Promise<{ filename: string; csv: string }> {
  const r = await fetch(apiUrl("/api/recons/inventory/je.csv"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ periodEnd, postingDate: periodEnd }),
    credentials: "include",
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  const disposition = r.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? `je-inv-${periodEnd}.csv`;
  return { filename, csv: await r.text() };
}

export function fetchFixedAssets(
  periodEnd: string,
): Promise<{
  snapshot: FixedAssetsSnapshot;
  result: FixedAssetsResult;
  source: "upload" | "fixture";
}> {
  return getJson(`/api/recons/fixed-assets?periodEnd=${periodEnd}`);
}

export async function downloadFixedAssetsJe(
  periodEnd: string,
): Promise<{ filename: string; csv: string }> {
  const r = await fetch(apiUrl("/api/recons/fixed-assets/je.csv"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ periodEnd, postingDate: periodEnd }),
    credentials: "include",
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  const disposition = r.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? `je-fa-${periodEnd}.csv`;
  return { filename, csv: await r.text() };
}

export function fetchLeaseRou(
  periodEnd: string,
): Promise<{
  snapshot: LeaseRouSnapshot;
  result: LeaseRouResult;
  source: "upload" | "fixture";
}> {
  return getJson(`/api/recons/lease-rou?periodEnd=${periodEnd}`);
}

export async function downloadLeaseRouJe(
  periodEnd: string,
): Promise<{ filename: string; csv: string }> {
  const r = await fetch(apiUrl("/api/recons/lease-rou/je.csv"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ periodEnd, postingDate: periodEnd }),
    credentials: "include",
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  const disposition = r.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? `je-lease-${periodEnd}.csv`;
  return { filename, csv: await r.text() };
}

export function fetchIntercompany(
  periodEnd: string,
): Promise<{
  snapshot: IntercompanySnapshot;
  result: IntercompanyResult;
  source: "upload" | "fixture";
}> {
  return getJson(`/api/recons/intercompany-cervos?periodEnd=${periodEnd}`);
}

export function fetchWorkpaper(
  reconId: string,
  period: string,
): Promise<WorkpaperState> {
  return getJson(`/api/workpapers/${reconId}/${period}`);
}

async function workpaperAction(
  reconId: string,
  period: string,
  action: "submit" | "approve" | "reopen",
): Promise<WorkpaperState> {
  const r = await fetch(
    apiUrl(`/api/workpapers/${reconId}/${period}/${action}`),
    { method: "POST", credentials: "include" },
  );
  if (!r.ok) {
    const body = await r.text();
    throw new Error(`${r.status}: ${body}`);
  }
  return r.json();
}

export const submitWorkpaper = (reconId: string, period: string) =>
  workpaperAction(reconId, period, "submit");
export const approveWorkpaper = (reconId: string, period: string) =>
  workpaperAction(reconId, period, "approve");
export const reopenWorkpaper = (reconId: string, period: string) =>
  workpaperAction(reconId, period, "reopen");

export async function downloadPoAccrualJe(
  periodEnd: string,
): Promise<{ filename: string; csv: string }> {
  const r = await fetch(apiUrl("/api/recons/po-accrual/je.csv"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ periodEnd, postingDate: periodEnd }),
    credentials: "include",
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  const disposition = r.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? `je-poaccrual-${periodEnd}.csv`;
  const csv = await r.text();
  return { filename, csv };
}

